// SPDX-License-Identifier: GPL-3.0-or-later
// What each effect level allows. The policies are fixed objects: a reader compares them by
// identity, and a call allocates nothing.
//
// No level has a screen shake, a full-screen flash, a camera move or a moving backdrop. Play is
// calm at every level (the "calm defaults" of the operator).
//
// - full: bursts, halos, the trail, the lamp show (a short, low-contrast wash) and the characters.
// - reduced: no lamp show and no character move (THEME.md 11.3). Fewer particles. Halos, the trail
//   and the bursts stay.
// - off: no effect at all. The lamps still show the state of the game.
// Reduced motion (the system setting or the in-game toggle) turns "full" into "reduced".
// Reduce flashing (a setting) turns "full" into "calm": no all-lamp wash, the rest of "full" stays.

import type { VfxLevel } from "../../shared/feedback/index.ts";

/** What one level allows. */
export interface VfxPolicy {
  /** Share of the full particle count, from 0 to 1. */
  readonly bursts: number;
  readonly halos: boolean;
  readonly trail: boolean;
  readonly lamps: boolean;
  /** The all-lamp colour wash of a moment. It needs `lamps` too. */
  readonly wash: boolean;
  readonly reactions: boolean;
}

/** Every effect that play allows. */
export const FULL_POLICY: VfxPolicy = {
  bursts: 1,
  halos: true,
  trail: true,
  lamps: true,
  wash: true,
  reactions: true,
};

/** Reduce flashing: every effect except the all-lamp wash. */
export const CALM_POLICY: VfxPolicy = { ...FULL_POLICY, wash: false };

/** No lamp show and no character move. Fewer particles. */
export const REDUCED_POLICY: VfxPolicy = {
  bursts: 0.4,
  halos: true,
  trail: true,
  lamps: false,
  wash: false,
  reactions: false,
};

/** Nothing. */
export const OFF_POLICY: VfxPolicy = {
  bursts: 0,
  halos: false,
  trail: false,
  lamps: false,
  wash: false,
  reactions: false,
};

/**
 * The policy of a level. Reduced motion lowers "full" to "reduced". Reduce flashing lowers "full"
 * to "calm". The level "off" and "reduced" win over reduce flashing.
 */
export function policyFor(
  level: VfxLevel,
  reducedMotion: boolean,
  reduceFlashing = false,
): VfxPolicy {
  if (level === "off") return OFF_POLICY;
  if (level === "reduced" || reducedMotion) return REDUCED_POLICY;
  return reduceFlashing ? CALM_POLICY : FULL_POLICY;
}
